import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { Button, ButtonGroup } from "react-bootstrap";

const ImageAnnotator = forwardRef(({ imageUrl, onUpdateImage }, ref) => {
  const canvasRef = useRef(null);
  const [scaledSize, setScaledSize] = useState({ width: 600, height: 400 });
  const [strokeColor, setStrokeColor] = useState("red");
  const [selectedColor, setSelectedColor] = useState("red");
  

  const MAX_WIDTH = 400;
  const MAX_HEIGHT = 500;

  // 🛠 Scale image to fit modal
  useEffect(() => {
    if (!imageUrl) return;

    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height, 1);
      setScaledSize({
        width: Math.round(width * scale),
        height: Math.round(height * scale),
      });
    };
  }, [imageUrl]);

  // ✅ Merge annotation and original image when modal closes
  const mergeImage = async () => {
    if (!canvasRef.current) return;
  
    try {
      // ✅ Get the annotation with a transparent background
      const annotationData = await canvasRef.current.exportImage("png");
  
      // ✅ Create an offscreen canvas (easier merging)
      const mergedCanvas = document.createElement("canvas");
      const ctx = mergedCanvas.getContext("2d");
  
      // ✅ Load the original image
      const background = new Image();
     background.crossOrigin = "anonymous"; // ✅ Prevents CORS issues IMPORTANT: WHITELIST YOUR DOMAIN IN BLOB STORAGE
      background.src = imageUrl;
  
      return new Promise((resolve, reject) => {
        background.onload = () => {
          mergedCanvas.width = background.width;
          mergedCanvas.height = background.height;
  
          // ✅ Draw the original image first
          ctx.drawImage(background, 0, 0, background.width, background.height);
  
          // ✅ Load and draw the annotation image on top
          const annotation = new Image();
          annotation.src = annotationData;
          annotation.onload = () => {
            ctx.drawImage(annotation, 0, 0, background.width, background.height);
  
            // ✅ Convert the merged canvas to a PNG image
            const finalImage = mergedCanvas.toDataURL("image/png");
            onUpdateImage(finalImage); // ✅ Update UI with final merged image
            resolve(finalImage);
          };
        };
  
        background.onerror = (err) => {
          console.error("⛔ Error loading background image:", err);
          reject(err);
        };
      });
    } catch (error) {
      console.error("⛔ Error merging images:", error);
    }
  };
  
  useImperativeHandle(ref, () => ({
    mergeImage, // ✅ This makes mergeImage() callable from FollowUpSession
  }));

  const handleColorChange = (color) => {
    switch (color) {
      case "red":
        setStrokeColor("red");
        setSelectedColor(color);
        break;
      case "primary":
        setStrokeColor("blue");
        setSelectedColor(color);
        break;
      case "secondary":
        setStrokeColor("teal");
        setSelectedColor(color);
        break;
      case "black":
        setStrokeColor("black");
        setSelectedColor(color);
        break;
      default:
        console.warn("Invalid color selected:", color);
    }
  };
  

  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          position: "relative",
          width: `${scaledSize.width}px`,
          height: `${scaledSize.height}px`,
          margin: "0 auto",
        }}
      >
        <img
          src={imageUrl}
          alt="Background"
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            zIndex: 0,
          }}
        />

        <ReactSketchCanvas
          ref={canvasRef}
          strokeWidth={3}
          strokeColor={strokeColor}
          width={scaledSize.width}
          height={scaledSize.height}
          canvasColor="transparent"
          allowOnlyPointerType="all"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1,
          }}
        />
      </div>

      {/* ✅ Keep Undo & Clear buttons */}
      <div className="mt-3">
        <Button variant="outline-primary" onClick={() => canvasRef.current?.clearCanvas()} className="m-1">
        <i className="fa-solid fa-eraser text-primary"></i>
        </Button>
        <Button variant="outline-primary" onClick={() => canvasRef.current?.undo()} className="m-1">
        <i class="fas fa-undo text-primary"></i>
        </Button>
        <ButtonGroup aria-label="Basic example">
      {/* ✅ Color Selection Buttons */}
          <Button variant="danger" onClick={() => handleColorChange("red", "red")}>
            {selectedColor === "red" && <i className="fa-solid fa-pencil"></i>}
          </Button>

          <Button variant="primary" onClick={() => handleColorChange("primary", "blue")}>
            {selectedColor === "primary" && <i className="fa-solid fa-pencil"></i>}
          </Button>

          <Button variant="secondary" onClick={() => handleColorChange("secondary", "gray")}>
            {selectedColor === "secondary" && <i className="fa-solid fa-pencil"></i>}
          </Button>

          <Button variant="dark" onClick={() => handleColorChange("black", "black")}>
            {selectedColor === "black" && <i className="fa-solid fa-pencil"></i>}
          </Button>
    </ButtonGroup>

      </div>
    </div>
  );
})

export default ImageAnnotator;
